import axios from "axios";
import { X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { getConfigWithToken } from "../config/config";
import { editUserById } from "../config/url";
import {
	getStaffIdLabel,
	getStaffRoles,
	hasProviderAccess,
	normalizeStaffRoles,
	STAFF_ROLES,
} from "../utils/staffRoles";
import classes from "./EditStaffDrawer.module.css";

const EDIT_ROLES = [
	{
		badgeClass: classes.badgeAdmin,
		description: "Full system access.",
		key: "Admin",
	},
	{
		badgeClass: classes.badgeProvider,
		description: "Delivers services. Has a caseload and appears in visit records.",
		key: "Provider",
	},
	{
		badgeClass: classes.badgeSupervisor,
		description: "Oversees providers. Can review and approve records.",
		key: "Supervisor",
	},
	{
		badgeClass: classes.badgeQa,
		description: "Quality assurance access - review records and flag issues.",
		key: "QA",
	},
].filter((role) => STAFF_ROLES.includes(role.key));

const emptyForm = {
	email: "",
	firstName: "",
	lastName: "",
	npi: "",
	phone: "",
	providerId: "",
	roles: [],
};

const formatPhoneInput = (value) => {
	const digits = String(value || "").replace(/\D/g, "").slice(0, 10);
	if (digits.length <= 3) return digits;
	if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
	return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const buildFormFromStaff = (staff) => ({
	email: staff?.email || "",
	firstName: staff?.firstname || "",
	lastName: staff?.lastname || "",
	npi: staff?.npi || staff?.providerInformation?.npi || "",
	phone: formatPhoneInput(staff?.phoneno || ""),
	providerId: staff?.userId || "",
	roles: getStaffRoles(staff),
});

const EditStaffDrawer = ({ isOpen, onClose, onSuccess, staff }) => {
	const currentUser = useSelector((state) => state?.user);
	const token = currentUser?.user?.token || localStorage.getItem("user_id");
	const organizationId =
		currentUser?.user?.organizationId || localStorage.getItem("organizationId") || localStorage.getItem("businessId");

	const [errors, setErrors] = useState({});
	const [form, setForm] = useState(emptyForm);
	const [initialRoles, setInitialRoles] = useState([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [showAdminWarning, setShowAdminWarning] = useState(false);

	const hasProviderRole = hasProviderAccess({ roles: form.roles });
	const staffIdLabel = getStaffIdLabel(form.roles);
	const isRemovingAdmin =
		initialRoles.includes("Admin") && !form.roles.includes("Admin") && form.roles.length > 0;

	const requestHeaders = useMemo(
		() => ({
			Accept: "application/json",
			"Content-Type": "application/json",
			...(token ? getConfigWithToken(token).headers : {}),
			...(organizationId ? { "x-organization-id": organizationId } : {}),
		}),
		[organizationId, token],
	);

	useEffect(() => {
		if (!isOpen) return undefined;
		const nextForm = buildFormFromStaff(staff);
		setForm(nextForm);
		setInitialRoles(nextForm.roles);
		setErrors({});
		setShowAdminWarning(false);
		const onKeyDown = (event) => {
			if (event.key === "Escape") onClose();
		};
		document.addEventListener("keydown", onKeyDown);
		document.body.style.overflow = "hidden";
		return () => {
			document.removeEventListener("keydown", onKeyDown);
			document.body.style.overflow = "";
		};
	}, [isOpen, onClose, staff]);

	if (!isOpen) return null;

	const updateField = (field, value) => {
		setForm((previous) => ({ ...previous, [field]: value }));
		setErrors((previous) => {
			const next = { ...previous };
			delete next[field];
			return next;
		});
	};

	const toggleRole = (roleKey) => {
		setForm((previous) => {
			const hasRole = previous.roles.includes(roleKey);
			const nextRoles = hasRole
				? previous.roles.filter((role) => role !== roleKey)
				: normalizeStaffRoles([...previous.roles, roleKey]);
			return {
				...previous,
				npi: nextRoles.includes("Provider") ? previous.npi : "",
				roles: nextRoles,
			};
		});
		setErrors((previous) => {
			const next = { ...previous };
			delete next.roles;
			delete next.npi;
			return next;
		});
	};

	const validate = () => {
		const nextErrors = {};
		if (!form.firstName.trim()) nextErrors.firstName = "First name is required.";
		if (!form.lastName.trim()) nextErrors.lastName = "Last name is required.";
		if (!form.providerId.trim()) nextErrors.providerId = `${staffIdLabel} is required.`;
		if (!form.roles.length) nextErrors.roles = "Select at least one role.";
		if (hasProviderRole && !form.npi.trim()) {
			nextErrors.npi = "NPI number is required for Provider access.";
		}
		const phoneDigits = form.phone.replace(/\D/g, "");
		if (phoneDigits && phoneDigits.length !== 10) {
			nextErrors.phone = "Phone number must be 10 digits.";
		}
		if (!organizationId) nextErrors.firstName = "Organization context is missing. Please sign in again.";
		setErrors(nextErrors);
		return Object.keys(nextErrors).length === 0;
	};

	const saveStaff = async () => {
		if (!staff?._id) return;

		setIsSubmitting(true);
		try {
			const phoneDigits = form.phone.replace(/\D/g, "");
			const payload = {
				firstname: form.firstName.trim(),
				lastname: form.lastName.trim(),
				npi: hasProviderRole ? form.npi.trim() : null,
				organization_id: organizationId,
				phoneno: phoneDigits || null,
				roles: form.roles,
				userId: form.providerId.trim(),
			};
			const response = await axios.post(editUserById(staff._id), payload, { headers: requestHeaders });
			if (response.data?.success === false || response.data?.success === "false") {
				toast.error(response.data?.message || "Failed to update staff.");
				return;
			}
			toast.success(response.data?.message || "Staff member updated successfully.");
			onSuccess?.();
			onClose();
		} catch (error) {
			toast.error(error.response?.data?.message || "Failed to update staff.");
		} finally {
			setIsSubmitting(false);
			setShowAdminWarning(false);
		}
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (!validate()) return;

		if (isRemovingAdmin && !showAdminWarning) {
			setShowAdminWarning(true);
			return;
		}

		await saveStaff();
	};

	return (
		<>
			<button aria-label="Close edit drawer" className={classes.backdrop} onClick={onClose} type="button" />
			<aside aria-labelledby="edit-staff-title" aria-modal="true" className={classes.drawer} role="dialog">
				<div className={classes.header}>
					<div className={classes.headerTop}>
						<h2 className={classes.title} id="edit-staff-title">
							Edit Staff Member
						</h2>
						<button aria-label="Close" className={classes.closeButton} onClick={onClose} type="button">
							<X size={18} />
						</button>
					</div>
					<p className={classes.subtitle}>Update staff information.</p>
				</div>

				<form className={classes.form} onSubmit={handleSubmit}>
					<div className={classes.body}>
						<div className={classes.nameRow}>
							<label className={classes.field}>
								<span className={classes.label}>
									First Name <span className={classes.required}>*</span>
								</span>
								<input
									className={`${classes.input} ${errors.firstName ? classes.inputError : ""}`}
									onChange={(event) => updateField("firstName", event.target.value)}
									placeholder="Sarah"
									type="text"
									value={form.firstName}
								/>
								{errors.firstName ? <p className={classes.errorText}>{errors.firstName}</p> : null}
							</label>

							<label className={classes.field}>
								<span className={classes.label}>
									Last Name <span className={classes.required}>*</span>
								</span>
								<input
									className={`${classes.input} ${errors.lastName ? classes.inputError : ""}`}
									onChange={(event) => updateField("lastName", event.target.value)}
									placeholder="Chen"
									type="text"
									value={form.lastName}
								/>
								{errors.lastName ? <p className={classes.errorText}>{errors.lastName}</p> : null}
							</label>
						</div>

						<label className={classes.field}>
							<span className={classes.label}>Email</span>
							<input className={`${classes.input} ${classes.inputReadOnly}`} readOnly type="email" value={form.email} />
						</label>

						<label className={classes.field}>
							<span className={classes.label}>
								Phone Number
							</span>
							<input
								className={`${classes.input} ${errors.phone ? classes.inputError : ""}`}
								inputMode="tel"
								onChange={(event) => updateField("phone", formatPhoneInput(event.target.value))}
								placeholder="(555) 123-4567"
								type="text"
								value={form.phone}
							/>
							{errors.phone ? <p className={classes.errorText}>{errors.phone}</p> : null}
						</label>

						<label className={classes.field}>
							<span className={classes.label}>
								{staffIdLabel} <span className={classes.required}>*</span>
							</span>
							<input
								className={`${classes.input} ${errors.providerId ? classes.inputError : ""}`}
								onChange={(event) => updateField("providerId", event.target.value)}
								placeholder={staffIdLabel}
								type="text"
								value={form.providerId}
							/>
							{errors.providerId ? <p className={classes.errorText}>{errors.providerId}</p> : null}
						</label>

						<div className={classes.field}>
							<span className={classes.label}>
								Roles <span className={classes.required}>*</span>
								<span className={classes.hint}>Select all that apply.</span>
							</span>
							<div className={classes.roleList}>
								{EDIT_ROLES.map((role) => {
									const isSelected = form.roles.includes(role.key);
									return (
										<label
											className={`${classes.roleOption} ${isSelected ? classes.roleOptionSelected : ""}`}
											key={role.key}>
											<input
												checked={isSelected}
												className={classes.checkbox}
												onChange={() => toggleRole(role.key)}
												type="checkbox"
											/>
											<span className={classes.roleContent}>
												<span className={`${classes.roleBadge} ${role.badgeClass}`}>{role.key}</span>
												<span className={classes.roleDescription}>{role.description}</span>
											</span>
										</label>
									);
								})}
							</div>
							{errors.roles ? <p className={classes.errorText}>{errors.roles}</p> : null}
						</div>

						{showAdminWarning ? (
							<div className={classes.warningBox}>
								<p className={classes.warningTitle}>Remove administrative access?</p>
								<p className={classes.warningText}>
									This staff member will lose Admin permissions in your organization. Historical records will not be
									deleted. Click Save again to confirm.
								</p>
							</div>
						) : null}

						{hasProviderRole ? (
							<div className={classes.providerSection}>
								<p className={classes.providerHeading}>Provider details (if Provider role selected)</p>
								<label className={classes.field}>
									<span className={classes.label}>
										NPI Number <span className={classes.required}>*</span>
									</span>
									<input
										className={`${classes.input} ${errors.npi ? classes.inputError : ""}`}
										inputMode="numeric"
										onChange={(event) =>
											updateField("npi", event.target.value.replace(/\D/g, "").slice(0, 10))
										}
										placeholder="1234567890"
										type="text"
										value={form.npi}
									/>
									{errors.npi ? <p className={classes.errorText}>{errors.npi}</p> : null}
								</label>
							</div>
						) : null}
					</div>

					<div className={classes.footer}>
						<button className={classes.cancelButton} onClick={onClose} type="button">
							Cancel
						</button>
						<button className={classes.submitButton} disabled={isSubmitting} type="submit">
							{isSubmitting ? "Saving..." : showAdminWarning ? "Confirm Save" : "Save"}
						</button>
					</div>
				</form>
			</aside>
		</>
	);
};

export default EditStaffDrawer;
